import { NeedDto, NeedsActionDto } from '@act/shared/data-transfer-objects';
import { Button } from '@act/storybook';
import { Typography } from '@mui/material';

import {
  ClockCounterClockwiseIcon,
  IdentificationBadgeIcon,
} from '@phosphor-icons/react';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import useAddActionDialog from '@act/react-components/dialog-forms/hooks/useAddActionDialog.hook';
import { metaSliceSelectors } from 'redux/slices/metaSlice.hook';
import { userAppSelectors } from 'redux/slices/usersSlice.slice';
import { generateDatadogTrackingProps } from 'toolkit/environment/environment/userMonitoring.util';
import {
  StyledHistoryEntry,
  StyledHistoryEntryDescription,
  StyledHistoryEntrySubheader,
  SubheaderAndDescription,
  getHistoryEntryDateLine,
} from './HistoryDisplay';
import './need-history.styles.scss';

interface ActionHistoryEntryProps {
  need: NeedDto;
  action: NeedsActionDto;
  detailMode?: boolean;
  editMode?: boolean;
}

const ActionHistoryEntry = ({
  need,
  action,
  detailMode,
  editMode,
}: ActionHistoryEntryProps) => {
  const userMap = userAppSelectors.useSelectUsersMap();
  const needMetadata = metaSliceSelectors.useSelectNeedsMetadata();
  const openAddActionDialog = useAddActionDialog();
  const activities = needMetadata?.data?.activities;
  const [latestAction, restOfActions] = useMemo(() => {
    const allActions = [action, ...(action.subsequentActions || [])];
    allActions.sort((a, b) => b.createdAt - a.createdAt);
    return [allActions[0], detailMode ? allActions.slice(1) : []];
  }, [action, detailMode]);

  const uniqueResourceCount = useMemo(() => {
    // Collect all resourceReferrals from main action and subsequentActions
    const allResourceReferrals = [
      ...(action.resourceReferrals || []),
      ...(action.subsequentActions || []).flatMap(
        sa => sa.resourceReferrals || []
      ),
    ];
    // Get unique resources by id
    const uniqueResourceIds = new Set(
      allResourceReferrals.map(ref => ref.id).filter(id => id !== undefined)
    );

    return uniqueResourceIds.size;
  }, [action]);

  function openEditActionDialog() {
    void openAddActionDialog({ need, action });
  }

  function getActionTitle(action: NeedsActionDto): string {
    const relevantAction = action?.subsequentActions?.length
      ? [...action?.subsequentActions]?.sort(
          (a, b) => b.createdAt - a.createdAt
        )[0]
      : action;

    return activities?.find(i => i.id === relevantAction?.activityId)?.label;
  }

  return (
    <StyledHistoryEntry
      header={
        <div className="history-entry-title-with-icon">
          <Typography variant="titleSmall">{getActionTitle(action)}</Typography>
          {!!action.subsequentActions?.length && (
            <ClockCounterClockwiseIcon
              color="primary"
              style={{ marginTop: '-2px', marginBottom: '-2px' }}
              size={16}
            />
          )}
        </div>
      }
      content={
        <div>
          <StyledHistoryEntrySubheader>
            {getHistoryEntryDateLine(
              DateTime.fromISO(latestAction.date).toMillis(),
              userMap && userMap[latestAction.creatorId]?.name
            )}
          </StyledHistoryEntrySubheader>
          <StyledHistoryEntryDescription>
            {latestAction.description}
          </StyledHistoryEntryDescription>
          {uniqueResourceCount > 0 && (
            <StyledHistoryEntryDescription>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  marginTop: '8px',
                }}
              >
                <IdentificationBadgeIcon size={16} />
                <Typography variant="bodySmall" className="resource-count">
                  {uniqueResourceCount} resource
                  {uniqueResourceCount !== 1 ? 's' : ''} referred
                </Typography>
              </div>
            </StyledHistoryEntryDescription>
          )}
        </div>
      }
      expandedContent={
        !!restOfActions.length && (
          <>
            {restOfActions.map(a => (
              <SubheaderAndDescription
                key={a.id}
                subheader={getHistoryEntryDateLine(
                  DateTime.fromISO(a.date).toMillis(),
                  userMap && userMap[a.creatorId]?.name
                )}
                description={a.description}
              />
            ))}
          </>
        )
      }
      footer={
        editMode && (
          <Button
            variant="text"
            onClick={() => openEditActionDialog()}
            {...generateDatadogTrackingProps('need-action-entry-edit')}
          >
            Edit
          </Button>
        )
      }
    />
  );
};

export default ActionHistoryEntry;
